import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    projectType: '',
    squareFootage: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Check if we're on homepage
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
    setIsMenuOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5j6l179u36dslcgh62g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyType: '',
          projectType: '',
          squareFootage: '',
          message: ''
        });
        setTimeout(() => {
          setShowQuoteForm(false);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-500 ${
          isScrolled || !isHomePage ? 'bg-white shadow-lg translate-y-0' : 'bg-transparent -translate-y-1'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24 sm:h-28">
            {/* Logo */}
            <h1 className="-ml-8 sm:-ml-12 lg:-ml-16">
              <a href="/" className="flex items-center hover:opacity-80 transition-opacity cursor-pointer">
                <img 
                  src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/e0b3cf16f09f7185e56ad37c0d6a5ff1.png" 
                  alt="Yuma Spray Foam Pros Logo" 
                  className="h-32 sm:h-36 lg:h-44 w-auto"
                />
              </a>
            </h1>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <a href="/" className={`font-medium transition-all duration-300 cursor-pointer flex items-center gap-2 hover:scale-105 ${isScrolled || !isHomePage ? 'text-gray-900 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
                <i className="ri-home-line"></i>
                Home
              </a>
              <a href="/about-yuma-insulation" className={`font-medium transition-all duration-300 cursor-pointer flex items-center gap-2 hover:scale-105 ${isScrolled || !isHomePage ? 'text-gray-900 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
                <i className="ri-information-line"></i>
                About
              </a>
              
              {/* Services Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className={`font-medium transition-all duration-300 flex items-center cursor-pointer gap-2 hover:scale-105 ${isScrolled || !isHomePage ? 'text-gray-900 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}
                >
                  <i className="ri-service-line"></i>
                  Services
                  <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {servicesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 z-50 animate-scale-in">
                    <a href="/insulation-services-arizona" className="block px-4 py-2 text-gray-900 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-all duration-200 hover:translate-x-1">
                      <i className="ri-list-check mr-2"></i>
                      Insulation Services
                    </a>
                    <div className="border-t border-gray-100 my-2"></div>
                    <a href="/residential-insulation-yuma" className="block px-4 py-2 text-gray-900 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-all duration-200 hover:translate-x-1">
                      <i className="ri-home-4-line mr-2"></i>
                      Residential Services
                    </a>
                    <a href="/commercial-insulation-yuma" className="block px-4 py-2 text-gray-900 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-all duration-200 hover:translate-x-1">
                      <i className="ri-building-line mr-2"></i>
                      Commercial Services
                    </a>
                    <div className="border-t border-gray-100 my-2"></div>
                    <a href="/closed-cell-spray-foam" className="block px-4 py-2 text-gray-900 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-all duration-200 hover:translate-x-1">
                      <i className="ri-drop-line mr-2"></i>
                      Closed Cell Foam
                    </a>
                    <a href="/open-cell-spray-foam" className="block px-4 py-2 text-gray-900 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-all duration-200 hover:translate-x-1">
                      <i className="ri-bubble-chart-line mr-2"></i>
                      Open Cell Foam
                    </a>
                    <div className="border-t border-gray-100 my-2"></div>
                    <a href="/yuma-projects" className="block px-4 py-2 text-gray-900 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-all duration-200 hover:translate-x-1">
                      <i className="ri-gallery-line mr-2"></i>
                      Projects
                    </a>
                  </div>
                )}
              </div>

              <a href="/insulation-faq" className={`font-medium transition-all duration-300 cursor-pointer flex items-center gap-2 hover:scale-105 ${isScrolled || !isHomePage ? 'text-gray-900 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
                <i className="ri-question-line"></i>
                FAQ
              </a>
              <a 
                href="tel:928-427-8140" 
                className="bg-white border-2 border-emerald-600 text-emerald-600 px-6 py-2 rounded-lg font-semibold hover:bg-emerald-600 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 hover:shadow-lg transform group"
              >
                <i className="ri-phone-line mr-2 group-hover:rotate-12 transition-transform duration-300"></i>
                (928) 427-8140
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 cursor-pointer transition-all duration-300 hover:scale-110 ${isScrolled || !isHomePage ? 'text-gray-900' : 'text-white'}`}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl transition-transform duration-300 ${mobileMenuOpen ? 'rotate-90' : ''}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden animate-fade-in">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="fixed top-24 sm:top-28 left-0 right-0 bg-white shadow-xl max-h-[calc(100vh-6rem)] sm:max-h-[calc(100vh-7rem)] overflow-y-auto animate-slide-in-down">
            <div className="px-4 py-4 space-y-3">
              <a href="/" onClick={() => setMobileMenuOpen(false)} className="block text-gray-900 font-medium hover:text-blue-600 cursor-pointer flex items-center gap-2 py-2 hover:translate-x-2 transition-all duration-300">
                <i className="ri-home-line text-lg"></i>
                <span className="text-sm">Home</span>
              </a>
              <a href="/about-yuma-insulation" onClick={() => setMobileMenuOpen(false)} className="block text-gray-900 font-medium hover:text-blue-600 cursor-pointer flex items-center gap-2 py-2 hover:translate-x-2 transition-all duration-300">
                <i className="ri-information-line text-lg"></i>
                <span className="text-sm">About</span>
              </a>
              
              {/* Mobile Services Section */}
              <div>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className="w-full flex items-center justify-between text-gray-900 font-medium hover:text-blue-600 cursor-pointer transition-colors duration-300 py-2"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-service-line text-lg"></i>
                    <span className="text-sm">Services</span>
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {servicesOpen && (
                  <div className="ml-4 mt-2 space-y-2 animate-slide-in-down">
                    <a href="/insulation-services-arizona" onClick={() => setMobileMenuOpen(false)} className="block text-gray-600 hover:text-blue-600 cursor-pointer hover:translate-x-2 transition-all duration-200 py-1.5 text-sm">
                      <i className="ri-list-check mr-2"></i>
                      Insulation Services
                    </a>
                    <a href="/residential-insulation-yuma" onClick={() => setMobileMenuOpen(false)} className="block text-gray-600 hover:text-blue-600 cursor-pointer hover:translate-x-2 transition-all duration-200 py-1.5 text-sm">
                      <i className="ri-home-4-line mr-2"></i>
                      Residential Services
                    </a>
                    <a href="/commercial-insulation-yuma" onClick={() => setMobileMenuOpen(false)} className="block text-gray-600 hover:text-blue-600 cursor-pointer hover:translate-x-2 transition-all duration-200 py-1.5 text-sm">
                      <i className="ri-building-line mr-2"></i>
                      Commercial Services
                    </a>
                    <a href="/closed-cell-spray-foam" onClick={() => setMobileMenuOpen(false)} className="block text-gray-600 hover:text-blue-600 cursor-pointer hover:translate-x-2 transition-all duration-200 py-1.5 text-sm">
                      <i className="ri-drop-line mr-2"></i>
                      Closed Cell Foam
                    </a>
                    <a href="/open-cell-spray-foam" onClick={() => setMobileMenuOpen(false)} className="block text-gray-600 hover:text-blue-600 cursor-pointer hover:translate-x-2 transition-all duration-200 py-1.5 text-sm">
                      <i className="ri-bubble-chart-line mr-2"></i>
                      Open Cell Foam
                    </a>
                    <a href="/yuma-projects" onClick={() => setMobileMenuOpen(false)} className="block text-gray-600 hover:text-blue-600 cursor-pointer hover:translate-x-2 transition-all duration-200 py-1.5 text-sm">
                      <i className="ri-gallery-line mr-2"></i>
                      Projects
                    </a>
                  </div>
                )}
              </div>

              <a href="/insulation-faq" onClick={() => setMobileMenuOpen(false)} className="block text-gray-900 font-medium hover:text-blue-600 cursor-pointer flex items-center gap-2 py-2 hover:translate-x-2 transition-all duration-300">
                <i className="ri-question-line text-lg"></i>
                <span className="text-sm">FAQ</span>
              </a>
              <a 
                href="tel:928-427-8140" 
                onClick={() => setMobileMenuOpen(false)}
                className="block bg-emerald-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-all duration-300 text-center cursor-pointer hover:scale-105 transform text-sm"
              >
                <i className="ri-phone-line mr-2"></i>
                (928) 427-8140
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Quote Form Modal */}
      {showQuoteForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6 rounded-t-xl sm:rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-lg sm:text-2xl font-bold">Request Your Free Quote</h2>
                <p className="text-blue-100 mt-1 text-xs sm:text-sm">We'll get back to you within 24 hours</p>
              </div>
              <button 
                onClick={() => setShowQuoteForm(false)}
                className="text-white hover:bg-white/20 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center transition-all cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-xl sm:text-2xl"></i>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} data-readdy-form id="navbar-quote-form" className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 sm:px-4 sm:py-3 rounded-lg flex items-center space-x-2 text-xs sm:text-sm">
                  <i className="ri-checkbox-circle-fill text-lg sm:text-xl"></i>
                  <span>Thank you! We'll contact you soon.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 sm:px-4 sm:py-3 rounded-lg flex items-center space-x-2 text-xs sm:text-sm">
                  <i className="ri-error-warning-line text-lg sm:text-xl"></i>
                  <span>Something went wrong. Please try again or call us directly.</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="group">
                  <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm transition-all duration-300"
                    placeholder="John Smith"
                  />
                </div>

                <div className="group">
                  <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm transition-all duration-300"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="group">
                  <label htmlFor="phone" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm transition-all duration-300"
                    placeholder="(928) 427-8140"
                  />
                </div>

                <div className="group">
                  <label htmlFor="propertyType" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                    Property Type *
                  </label>
                  <div className="relative">
                    <select
                      id="propertyType"
                      name="propertyType"
                      required
                      value={formData.propertyType}
                      onChange={(e) => setFormData({...formData, propertyType: e.target.value})}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 pr-8 sm:pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm cursor-pointer transition-all duration-300 appearance-none bg-white"
                    >
                      <option value="">Select property type</option>
                      <option value="Residential">Residential</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Industrial">Industrial</option>
                    </select>
                    <div className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-500 text-lg sm:text-xl transition-transform duration-300 group-focus-within:text-blue-600 group-focus-within:rotate-180"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="group">
                  <label htmlFor="projectType" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                    Service Needed *
                  </label>
                  <div className="relative">
                    <select
                      id="projectType"
                      name="projectType"
                      required
                      value={formData.projectType}
                      onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 pr-8 sm:pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm cursor-pointer transition-all duration-300 appearance-none bg-white"
                    >
                      <option value="">Select service</option>
                      <option value="Attic Insulation">Attic Insulation</option>
                      <option value="Wall Insulation">Wall Insulation</option>
                      <option value="Closed-Cell Spray Foam">Closed-Cell Spray Foam</option>
                      <option value="Open-Cell Spray Foam">Open-Cell Spray Foam</option>
                      <option value="Crawl Space">Crawl Space</option>
                      <option value="Garage">Garage</option>
                      <option value="Commercial Project">Commercial Project</option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-500 text-lg sm:text-xl transition-transform duration-300 group-focus-within:text-blue-600 group-focus-within:rotate-180"></i>
                    </div>
                  </div>
                </div>

                <div className="group">
                  <label htmlFor="squareFootage" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                    Approximate Square Footage
                  </label>
                  <input
                    type="text"
                    id="squareFootage"
                    name="squareFootage"
                    value={formData.squareFootage}
                    onChange={(e) => setFormData({...formData, squareFootage: e.target.value})}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm transition-all duration-300"
                    placeholder="e.g., 2,000 sq ft"
                  />
                </div>
              </div>

              <div className="group">
                <label htmlFor="message" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                  Project Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm resize-none transition-all duration-300"
                  placeholder="Tell us about your project..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-emerald-600 text-white py-3 sm:py-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                {isSubmitting ? 'Sending...' : 'Submit Quote Request'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
